/**
 * The route table (TanStack Router, code-based — no generator, no plugin).
 *
 *     /login              LoginView            public
 *     (_authed)           AppLayout            the guard: signed out → /login?redirect=<here>
 *       /                 → /rundowns
 *       /rundowns         RundownsView
 *       /rundowns/$id     RundownEditorView  (keyed by the id: another rundown is a fresh session)
 *       /productions      ProductionsView
 *       /productions/$id  PlayoutView        (keyed: another production is a fresh session)
 *       /cloud-productions CloudProductionsView (launch, stop; a cloud production plays out at /productions/$id)
 *       /studios          StudiosView
 *       /studios/$id      StudioView           (devices, the mapping table against a show style, JSON)
 *       /show-styles      ShowStylesView       (D32)
 *       /show-styles/$id  ShowStyleView
 *       /whoami           WhoamiView
 *       /vendor           VendorLayout         (the staff console's sections, D39: only on the console host's shell)
 *         /ops, /tenants, /tenants/$id, /releases, /rollouts (7f part B), /announcements, /announcements/$id (or `new`), /audit
 *       /alerts           AlertsView           (phase 7b: active alerts and the history)
 *       /alerts/settings  AlertSettingsView    (rules and channels; tenant.admin)
 *       /onboarding       OnboardingView       (the first-run wizard; re-opened from “Setup”)
 *       /account          AccountView          (my offline PIN, Phase 7b)
 *       /admin            AdminLayout          (Phase 7b: tenant.admin / billing.view; the cloud's) → its first section
 *         /admin/users | groups | groups/$id | sites | sites/$id | secrets | audit | usage
 *     /accept-invite      AcceptInviteView     public: the invite link's set-password page (token in the fragment)
 *
 * TWO SHELLS, ONE TREE (D39). The signed-in route renders the customer app's `AppLayout`, or — on the console host
 * (`services.shell === 'console'`) — the staff console's `ConsoleLayout`, which keeps only its own pages
 * (`lib/console.ts`' `consoleAllows`: /vendor/*, the Admin area's users and groups, /account, /whoami) and sends every
 * other path to its home.
 *
 * ADDING A PAGE: write the view in `src/views/` (a container: hooks in,
 * presentational components out), add a `createRoute({ getParentRoute: () =>
 * authedRoute, path, component })` below and list it in `routeTree`, and — if
 * it belongs in the top bar — add it to `NAV` in `views/AppLayout.tsx`.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, Navigate, Outlet, redirect, type RouterHistory } from '@tanstack/react-router';
import { Toaster } from '../components/ui/sonner.js';
import { AlertSettingsView } from '../views/AlertSettingsView.js';
import { AlertsView } from '../views/AlertsView.js';
import { AppLayout } from '../views/AppLayout.js';
import { OnboardingView } from '../views/OnboardingView.js';
import { CloudProductionsView } from '../views/CloudProductionsView.js';
import { LoginView } from '../views/LoginView.js';
import { PlayoutView } from '../views/PlayoutView.js';
import { ProductionsView } from '../views/ProductionsView.js';
import { RedundancyView } from '../views/RedundancyView.js';
import { RundownEditorView } from '../views/RundownEditorView.js';
import { RundownsView } from '../views/RundownsView.js';
import { ShowStylesView } from '../views/ShowStylesView.js';
import { ShowStyleView } from '../views/ShowStyleView.js';
import { StudiosView } from '../views/StudiosView.js';
import { StudioView } from '../views/StudioView.js';
import { WhoamiView } from '../views/WhoamiView.js';
import { VendorAnnouncementsView } from '../views/VendorAnnouncementsView.js';
import { VendorAnnouncementView } from '../views/VendorAnnouncementView.js';
import { VendorAuditView } from '../views/VendorAuditView.js';
import { VendorLayout } from '../views/VendorLayout.js';
import { VendorOpsView } from '../views/VendorOpsView.js';
import { VendorReleasesView } from '../views/VendorReleasesView.js';
import { VendorTenantsView } from '../views/VendorTenantsView.js';
import { VendorTenantView } from '../views/VendorTenantView.js';
import { AcceptInviteView } from '../views/AcceptInviteView.js';
import { AccountView } from '../views/AccountView.js';
import { AdminLayout } from '../views/admin/AdminLayout.js';
import { AuditView } from '../views/admin/AuditView.js';
import { DeviceSecretsView } from '../views/admin/DeviceSecretsView.js';
import { GroupAdminView } from '../views/admin/GroupAdminView.js';
import { GroupsAdminView } from '../views/admin/GroupsAdminView.js';
import { SiteAdminView } from '../views/admin/SiteAdminView.js';
import { SitesAdminView } from '../views/admin/SitesAdminView.js';
import { UsageView } from '../views/admin/UsageView.js';
import { UsersAdminView } from '../views/admin/UsersAdminView.js';
import { useGrants, useMe, useServices, useTheme } from './hooks.js';
import { ConsoleLayout } from '../views/console/ConsoleLayout.js';
import { ConsoleRolloutsView } from '../views/console/ConsoleRolloutsView.js';
import { CONSOLE_HOME, shellAdminLanding } from '../lib/console.js';
import type { Services } from './services.js';
import { guardAuthed, HOME_PATH } from '../lib/redirect.js';

export type RouterContext = { readonly services: Services; readonly queryClient: QueryClient };

function RootLayout() {
  const theme = useTheme();
  return (
    <>
      <Outlet />
      <Toaster theme={theme} position="bottom-right" />
    </>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => (typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {}),
  component: LoginView,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ context, location }) => {
    const bounce = guardAuthed(context.services.sessions.getSession(), location.href);
    if (bounce !== null) throw redirect(bounce);
  },
  component: SignedInLayout,
});

/** The host's shell (D39): the staff console on the console host, the customer app everywhere else. */
function SignedInLayout() {
  return useServices().shell === 'console' ? <ConsoleLayout /> : <AppLayout />;
}

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    throw redirect({ to: context.services.shell === 'console' ? CONSOLE_HOME : HOME_PATH });
  },
});

const rundownsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/rundowns', component: RundownsView });
const whoamiRoute = createRoute({ getParentRoute: () => authedRoute, path: '/whoami', component: WhoamiView });

const rundownEditorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rundowns/$rundownId',
  component: function RundownEditorPage() {
    const { rundownId } = rundownEditorRoute.useParams();
    return <RundownEditorView key={rundownId} rundownId={rundownId} />;
  },
});

const productionsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/productions', component: ProductionsView });

const playoutRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/productions/$productionId',
  component: function PlayoutPage() {
    const { productionId } = playoutRoute.useParams();
    return <PlayoutView key={productionId} productionId={productionId} />;
  },
});

const cloudProductionsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/cloud-productions', component: CloudProductionsView });

/** Main/backup diagnostics and operations (wave 7b-tail C). */
const redundancyRoute = createRoute({ getParentRoute: () => authedRoute, path: '/redundancy', component: RedundancyView });

const studiosRoute = createRoute({ getParentRoute: () => authedRoute, path: '/studios', component: StudiosView });

const studioRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/studios/$studioId',
  component: function StudioPage() {
    const { studioId } = studioRoute.useParams();
    return <StudioView key={studioId} studioId={studioId} />;
  },
});

const showStylesRoute = createRoute({ getParentRoute: () => authedRoute, path: '/show-styles', component: ShowStylesView });

const showStyleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/show-styles/$showStyleId',
  component: function ShowStylePage() {
    const { showStyleId } = showStyleRoute.useParams();
    return <ShowStyleView key={showStyleId} showStyleId={showStyleId} />;
  },
});

// ─── the vendor console (phase 7b): its own layout, the sections under it ───

const vendorRoute = createRoute({ getParentRoute: () => authedRoute, path: '/vendor', component: VendorLayout });
const vendorIndexRoute = createRoute({
  getParentRoute: () => vendorRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: CONSOLE_HOME });
  },
});
const vendorOpsRoute = createRoute({ getParentRoute: () => vendorRoute, path: '/ops', component: VendorOpsView });
const vendorTenantsRoute = createRoute({ getParentRoute: () => vendorRoute, path: '/tenants', component: VendorTenantsView });
const vendorTenantRoute = createRoute({
  getParentRoute: () => vendorRoute,
  path: '/tenants/$tenantId',
  component: function VendorTenantPage() {
    const { tenantId } = vendorTenantRoute.useParams();
    return <VendorTenantView key={tenantId} tenantId={tenantId} />;
  },
});
const vendorReleasesRoute = createRoute({ getParentRoute: () => vendorRoute, path: '/releases', component: VendorReleasesView });
/** D40's rollout rings (phase 7f part B): the rollout, its rings' gates and the vendor's actions. */
const vendorRolloutsRoute = createRoute({ getParentRoute: () => vendorRoute, path: '/rollouts', component: ConsoleRolloutsView });
const vendorAnnouncementsRoute = createRoute({ getParentRoute: () => vendorRoute, path: '/announcements', component: VendorAnnouncementsView });
const vendorAnnouncementRoute = createRoute({
  getParentRoute: () => vendorRoute,
  path: '/announcements/$announcementId',
  component: function VendorAnnouncementPage() {
    const { announcementId } = vendorAnnouncementRoute.useParams();
    return <VendorAnnouncementView key={announcementId} announcementId={announcementId} />;
  },
});
const vendorAuditRoute = createRoute({ getParentRoute: () => vendorRoute, path: '/audit', component: VendorAuditView });

const vendorTree = vendorRoute.addChildren([vendorIndexRoute, vendorOpsRoute, vendorTenantsRoute, vendorTenantRoute, vendorReleasesRoute, vendorRolloutsRoute, vendorAnnouncementsRoute, vendorAnnouncementRoute, vendorAuditRoute]);

const alertsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/alerts', component: AlertsView });
const alertSettingsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/alerts/settings', component: AlertSettingsView });
const onboardingRoute = createRoute({ getParentRoute: () => authedRoute, path: '/onboarding', component: OnboardingView });

// ─── Phase 7b: my account, the Admin area, the invite link ───

const acceptInviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/accept-invite', component: AcceptInviteView });

const accountRoute = createRoute({ getParentRoute: () => authedRoute, path: '/account', component: AccountView });

const adminRoute = createRoute({ getParentRoute: () => authedRoute, path: '/admin', component: AdminLayout });

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: function AdminIndex() {
    const me = useMe();
    const target = shellAdminLanding(useGrants(), useServices().shell);
    // Wait for /me before choosing (unknown grants would pick Users for a billing-only user); none to show: the layout says so.
    if (me.data === undefined) return null;
    return target === '/admin' ? null : <Navigate to={target} replace />;
  },
});

const adminUsersRoute = createRoute({ getParentRoute: () => adminRoute, path: '/users', component: UsersAdminView });
const adminGroupsRoute = createRoute({ getParentRoute: () => adminRoute, path: '/groups', component: GroupsAdminView });
const adminGroupRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/groups/$groupId',
  component: function AdminGroupPage() {
    const { groupId } = adminGroupRoute.useParams();
    return <GroupAdminView key={groupId} groupId={groupId} />;
  },
});
const adminSitesRoute = createRoute({ getParentRoute: () => adminRoute, path: '/sites', component: SitesAdminView });
const adminSiteRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/sites/$siteId',
  component: function AdminSitePage() {
    const { siteId } = adminSiteRoute.useParams();
    return <SiteAdminView key={siteId} siteId={siteId} />;
  },
});
const adminSecretsRoute = createRoute({ getParentRoute: () => adminRoute, path: '/secrets', component: DeviceSecretsView });
const adminAuditRoute = createRoute({ getParentRoute: () => adminRoute, path: '/audit', component: AuditView });
const adminUsageRoute = createRoute({ getParentRoute: () => adminRoute, path: '/usage', component: UsageView });

export const routeTree = rootRoute.addChildren([
  loginRoute,
  acceptInviteRoute,
  authedRoute.addChildren([
    indexRoute,
    rundownsRoute,
    rundownEditorRoute,
    productionsRoute,
    playoutRoute,
    cloudProductionsRoute,
    redundancyRoute,
    studiosRoute,
    studioRoute,
    showStylesRoute,
    showStyleRoute,
    whoamiRoute,
    accountRoute,
    alertsRoute,
    alertSettingsRoute,
    onboardingRoute,
    vendorTree,
    adminRoute.addChildren([adminIndexRoute, adminUsersRoute, adminGroupsRoute, adminGroupRoute, adminSitesRoute, adminSiteRoute, adminSecretsRoute, adminAuditRoute, adminUsageRoute]),
  ]),
]);

/** `history`: the browser's in the app, a memory history in tests (the router is otherwise the real one). */
export function createAppRouter(context: RouterContext, history: RouterHistory) {
  return createRouter({ routeTree, context, history, defaultPreload: false });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
